import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  hint?: string;
  danger?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  separator?: false;
}
/** `node`: something of its own at that place (the account card at the top of the account menu). */
export type MenuEntry = MenuItem | { separator: true } | { heading: string } | { node: ReactNode };

/** What a popover hangs off: an element, or a point (context menus open at the cursor). */
export type Anchor = Pick<HTMLElement, "getBoundingClientRect" | "contains">;

export function pointAnchor(x: number, y: number): Anchor {
  return { getBoundingClientRect: () => new DOMRect(x, y, 0, 0), contains: () => false };
}

/** A hint that is a keyboard shortcut ("Ctrl+,", "F10"): hidden on touch screens, where it means nothing. */
const KEYS = /^(Ctrl|Alt|Shift|Cmd|⌘|F\d)/;

/** Popover anchored to an element; closes on outside click, Escape, or selection. */
export function Popover({
  anchor,
  onClose,
  children,
  align = "start",
  side = "bottom",
  className = "",
}: {
  anchor: Anchor | null;
  onClose: () => void;
  children: ReactNode;
  align?: "start" | "end";
  side?: "bottom" | "right" | "top";
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!anchor || !el) return;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const m = el.getBoundingClientRect();
      let top = side === "right" ? a.top : side === "top" ? a.top - m.height - 4 : a.bottom + 4;
      let left = side === "right" ? a.right + 4 : align === "end" ? a.right - m.width : a.left;
      // A submenu with no room on the right opens on the left of its parent menu, like Evernote's.
      if (side === "right" && left + m.width > window.innerWidth - 8) {
        const parent = ((anchor instanceof Element && anchor.closest(".popover")) || anchor).getBoundingClientRect();
        left = parent.left - m.width - 4;
      }
      top = Math.max(8, Math.min(top, window.innerHeight - m.height - 8));
      left = Math.max(8, Math.min(left, window.innerWidth - m.width - 8));
      setPos((p) => (p && p.top === top && p.left === left ? p : { top, left }));
    };
    place();
    // Menus with sub-views (filters, pickers) change size; keep them on screen.
    const ro = new ResizeObserver(place);
    ro.observe(el);
    return () => ro.disconnect();
  }, [anchor, align, side]);

  useEffect(() => {
    const down = (e: MouseEvent) => {
      if (ref.current?.contains(e.target as Node) || anchor?.contains(e.target as Node)) return;
      // A submenu is its own popover; clicks in it belong to this one too.
      if ((e.target as Element).closest?.(".popover")) return;
      onClose();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
    };
  }, [anchor, onClose]);

  return createPortal(
    // Before it's placed the popover is transparent, not visibility:hidden: hidden elements can't take focus, and
    // inputs inside (search boxes) use autoFocus.
    <div ref={ref} className={`popover ${className}`} style={pos ? { top: pos.top, left: pos.left } : { opacity: 0, pointerEvents: "none", top: 0, left: 0 }}>
      {children}
    </div>,
    document.body,
  );
}

export function Menu({ anchor, entries, onClose, align, side }: { anchor: Anchor | null; entries: MenuEntry[]; onClose: () => void; align?: "start" | "end"; side?: "bottom" | "right" | "top" }) {
  return (
    <Popover anchor={anchor} onClose={onClose} align={align} side={side} className="menu">
      {entries.map((e, i) =>
        "heading" in e ? (
          <div key={i} className="menu-heading">{e.heading}</div>
        ) : "node" in e ? (
          <div key={i}>{e.node}</div>
        ) : e.separator ? (
          <div key={i} className="menu-sep" />
        ) : (
          <button
            key={i}
            className={`menu-item${e.danger ? " danger" : ""}`}
            disabled={e.disabled}
            onClick={() => {
              onClose();
              e.onSelect?.();
            }}
          >
            <span className="menu-icon">{e.icon}</span>
            <span className="menu-label">{e.label}</span>
            {e.checked && <span className="menu-check">✓</span>}
            {e.hint && <span className={`menu-hint${KEYS.test(e.hint) ? " key" : ""}`}>{e.hint}</span>}
          </button>
        ),
      )}
    </Popover>
  );
}

/** Hook for a button that toggles a popover. */
export function useAnchor() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return {
    anchor,
    open: (e: { currentTarget: HTMLElement }) => {
      // Read currentTarget now: React clears it after dispatch, and the updater may run later.
      const el = e.currentTarget;
      setAnchor((a) => (a ? null : el));
    },
    close: () => setAnchor(null),
  };
}

/** Small modal prompt (rename, new notebook, …). */
export function Prompt({
  title,
  label,
  initial = "",
  confirm = "Save",
  validate,
  onSubmit,
  onClose,
}: {
  title: string;
  label?: string;
  initial?: string;
  confirm?: string;
  /** Returns an error to show instead of submitting (e.g. a name that's already used). */
  validate?: (v: string) => Promise<string | null> | string | null;
  onSubmit: (v: string) => void;
  onClose: () => void;
}) {
  const [v, setV] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const busy = useRef(false);
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        onSubmit={async (e) => {
          e.preventDefault();
          const value = v.trim();
          if (!value || busy.current) return;
          busy.current = true;
          const problem = (await validate?.(value)) ?? null;
          busy.current = false;
          if (problem) return setError(problem);
          onSubmit(value);
          onClose();
        }}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
      >
        <h2>{title}</h2>
        {label && <label className="modal-label">{label}</label>}
        <input
          className={`input${error ? " invalid" : ""}`}
          autoFocus
          value={v}
          onChange={(e) => {
            setV(e.target.value);
            setError(null);
          }}
          onFocus={(e) => e.target.select()}
        />
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!v.trim()}>{confirm}</button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

export function Confirm({ title, body, confirm, danger, onConfirm, onClose }: { title: string; body: ReactNode; confirm: string; danger?: boolean; onConfirm: () => void; onClose: () => void }) {
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <h2>{title}</h2>
        <div className="modal-body">{body}</div>
        <div className="modal-actions">
          <button className="btn" onClick={onClose} autoFocus>Cancel</button>
          <button
            className={`btn ${danger ? "danger" : "primary"}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirm}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
